// The word sets (Matt, 2026-08-29: "some difficulty options and maybe a few
// themed ones"). Each is curated by the same standard as the classic list -
// short, family-safe, guessable - but pointed somewhere:
//
// - EASY is vocabulary a young kid has: concrete things you can point at.
//   The ambiguity that makes clues interesting mostly comes from the clue,
//   not the board.
// - TRICKY leans the other way: abstractions, figurative words, and
//   heteronym traps (DESERT, PRESENT, NOVEL...) where even parsing the
//   board word is part of the game.
// - The themes narrow the WORLD instead of the difficulty. A themed board
//   is genuinely harder for the spymaster - everything already relates to
//   everything - which is what makes it funny.
//
// Every set is comfortably bigger than the 25 a board needs, so rematches
// on the same set deal fresh boards. Sets must hold single tokens in ALL
// CAPS; tests enforce size, uniqueness, and shape.

import { WORDS } from './words';

const EASY = [
  'DOG', 'CAT', 'FISH', 'BIRD', 'HORSE', 'COW', 'PIG', 'DUCK', 'FROG', 'BEAR',
  'LION', 'TIGER', 'MONKEY', 'ZEBRA', 'SNAKE', 'MOUSE', 'RABBIT', 'TURTLE',
  'SHARK', 'WHALE', 'BEE', 'ANT', 'SPIDER', 'APPLE', 'BANANA', 'ORANGE',
  'GRAPE', 'CAKE', 'COOKIE', 'CANDY', 'MILK', 'EGG', 'BREAD', 'CHEESE',
  'PIZZA', 'CORN', 'CARROT', 'HOUSE', 'DOOR', 'WINDOW', 'BED', 'CHAIR',
  'TABLE', 'LAMP', 'CLOCK', 'PHONE', 'BOOK', 'PENCIL', 'PAPER', 'SCHOOL',
  'BUS', 'CAR', 'TRUCK', 'TRAIN', 'PLANE', 'BOAT', 'BIKE', 'BALL', 'KITE',
  'DOLL', 'ROBOT', 'GAME', 'SHOE', 'SOCK', 'HAT', 'COAT', 'SHIRT', 'TREE',
  'FLOWER', 'GRASS', 'LEAF', 'SUN', 'MOON', 'STAR', 'CLOUD', 'RAIN', 'SNOW',
  'WIND', 'FIRE', 'WATER', 'ROCK', 'SAND', 'BEACH', 'RIVER', 'LAKE',
  'MOUNTAIN', 'FARM', 'ZOO', 'PARK', 'KING', 'QUEEN', 'BABY', 'DOCTOR',
  'TEACHER', 'PIRATE', 'CLOWN', 'DRUM', 'HORN', 'BELL', 'KEY', 'BOX',
  'BALLOON', 'CRAYON', 'PUZZLE', 'SWING', 'SLIDE', 'POOL', 'TENT', 'MAP',
];

const TRICKY = [
  // Abstractions and figurative territory.
  'MEMORY', 'SHADOW', 'SECRET', 'RUMOR', 'ECHO', 'RIDDLE', 'PARADOX',
  'IRONY', 'VIRTUE', 'VICE', 'THEORY', 'MOTIVE', 'ALIBI', 'CIPHER', 'OMEN',
  'FATE', 'DESTINY', 'CHAOS', 'ORDER', 'JUSTICE', 'MERCY', 'PRIDE', 'ENVY',
  'GREED', 'WISDOM', 'COURAGE', 'FEAR', 'DREAM', 'NIGHTMARE', 'TRANCE',
  'AURA', 'KARMA', 'LIMBO', 'VOID', 'ABYSS', 'MIRAGE', 'ILLUSION',
  'PHANTOM', 'WHIM', 'GRUDGE', 'HUNCH', 'JINX', 'TABOO', 'HERESY', 'EXILE',
  'TRUCE', 'SIEGE', 'AMBUSH', 'DECOY', 'RANSOM', 'BOUNTY', 'VERDICT',
  'PARDON', 'TREASON', 'SCANDAL', 'HOAX', 'FRAUD', 'BLUFF', 'GAMBIT',
  'STALEMATE', 'VERTIGO', 'VENDETTA', 'NOSTALGIA', 'MELANCHOLY', 'EUPHORIA',
  'HUBRIS', 'NEMESIS', 'MUSE', 'ENIGMA', 'ANOMALY', 'ENTROPY', 'INFINITY',
  // Heteronyms and double-parses - the board word itself is a trap.
  'DESERT', 'PRESENT', 'NOVEL', 'PATENT', 'REFRAIN', 'COMPOUND', 'CONTRACT',
  'CONSOLE', 'OBJECT', 'SUBJECT', 'PROJECT', 'PERMIT', 'CONDUCT', 'EXPLOIT',
  'INVALID', 'MINUTE', 'BASS', 'DOVE', 'WOUND', 'TEAR', 'BOW', 'LEAD',
  'WIND', 'ROW', 'SOW', 'CLOSE', 'CONTENT', 'DIGEST', 'ESCORT', 'INCENSE',
  'INTIMATE', 'MODERATE', 'PRODUCE', 'PROTEST', 'REBEL', 'RECORD', 'REFUSE',
  'SUSPECT', 'ADDRESS', 'ATTRIBUTE', 'COMPRESS', 'CONVICT', 'DEFECT',
];

const FANTASY = [
  'DRAGON', 'WIZARD', 'WITCH', 'KNIGHT', 'CASTLE', 'QUEST', 'SWORD',
  'SHIELD', 'POTION', 'SPELL', 'WAND', 'CURSE', 'PROPHECY', 'SCROLL',
  'RUNE', 'GOBLIN', 'TROLL', 'OGRE', 'ELF', 'DWARF', 'FAIRY', 'GIANT',
  'UNICORN', 'GRIFFIN', 'PHOENIX', 'HYDRA', 'KRAKEN', 'MERMAID', 'CENTAUR',
  'MINOTAUR', 'CYCLOPS', 'GENIE', 'GHOST', 'VAMPIRE', 'WEREWOLF', 'ZOMBIE',
  'SKELETON', 'DUNGEON', 'TOWER', 'THRONE', 'CROWN', 'KINGDOM', 'EMPIRE',
  'REALM', 'PORTAL', 'ORACLE', 'ALTAR', 'TEMPLE', 'SHRINE', 'RELIC',
  'AMULET', 'TALISMAN', 'CRYSTAL', 'ORB', 'STAFF', 'CLOAK', 'ARMOR',
  'HELM', 'LANCE', 'ARROW', 'AXE', 'HAMMER', 'FORGE', 'ANVIL', 'TAVERN',
  'BARD', 'JESTER', 'SQUIRE', 'PALADIN', 'ROGUE', 'RANGER', 'DRUID',
  'SORCERER', 'ALCHEMY', 'ELIXIR', 'GRIMOIRE', 'FAMILIAR', 'RAVEN', 'WOLF',
  'SERPENT', 'BEAST', 'LAIR', 'CAVERN', 'LABYRINTH', 'TREASURE', 'HOARD',
  'JEWEL', 'CHALICE', 'SIREN', 'GOLEM', 'IMP', 'SPRITE', 'WYVERN', 'NYMPH',
];

const SCIENCE = [
  'ROCKET', 'PLANET', 'COMET', 'ASTEROID', 'METEOR', 'GALAXY', 'NEBULA',
  'ORBIT', 'GRAVITY', 'ATOM', 'MOLECULE', 'ELECTRON', 'PROTON', 'NEUTRON',
  'QUARK', 'LASER', 'MAGNET', 'CIRCUIT', 'BATTERY', 'ROBOT', 'SATELLITE',
  'TELESCOPE', 'MICROSCOPE', 'BEAKER', 'FLASK', 'LAB', 'EXPERIMENT',
  'THEORY', 'FORMULA', 'EQUATION', 'DATA', 'CODE', 'VIRUS', 'BACTERIA',
  'CELL', 'GENE', 'CLONE', 'FOSSIL', 'DINOSAUR', 'EVOLUTION', 'ENERGY',
  'FORCE', 'MOTION', 'FRICTION', 'VACUUM', 'PLASMA', 'PRISM', 'SPECTRUM',
  'RADIATION', 'ISOTOPE', 'ELEMENT', 'CARBON', 'OXYGEN', 'HYDROGEN',
  'HELIUM', 'NEON', 'MERCURY', 'VENUS', 'MARS', 'JUPITER', 'SATURN',
  'NEPTUNE', 'PLUTO', 'ECLIPSE', 'COSMOS', 'UNIVERSE', 'DIMENSION',
  'QUANTUM', 'WORMHOLE', 'SUPERNOVA', 'PULSAR', 'QUASAR', 'ASTRONAUT',
  'SHUTTLE', 'STATION', 'MODULE', 'CAPSULE', 'PROBE', 'ROVER', 'ANTENNA',
  'SIGNAL', 'FREQUENCY', 'WAVE', 'PARTICLE', 'FUSION', 'REACTOR', 'TURBINE',
  'PISTON', 'GEAR', 'LEVER', 'PULLEY', 'PENDULUM', 'COMPASS', 'RADAR',
];

const FOOD = [
  'PIZZA', 'TACO', 'BURGER', 'PASTA', 'NOODLE', 'SUSHI', 'BACON', 'WAFFLE',
  'PANCAKE', 'SYRUP', 'HONEY', 'BUTTER', 'TOAST', 'BAGEL', 'DONUT',
  'MUFFIN', 'COOKIE', 'BROWNIE', 'FUDGE', 'CARAMEL', 'CHOCOLATE', 'VANILLA',
  'MINT', 'GINGER', 'CINNAMON', 'PEPPER', 'SALT', 'SUGAR', 'FLOUR', 'DOUGH',
  'YEAST', 'CHEESE', 'YOGURT', 'CREAM', 'LATTE', 'MOCHA', 'ESPRESSO',
  'COFFEE', 'TEA', 'JUICE', 'SODA', 'LEMONADE', 'SMOOTHIE', 'MILKSHAKE',
  'PICKLE', 'OLIVE', 'ONION', 'GARLIC', 'TOMATO', 'POTATO', 'CARROT',
  'CELERY', 'LETTUCE', 'SPINACH', 'BROCCOLI', 'PEA', 'BEAN', 'RICE', 'CORN',
  'MANGO', 'PEACH', 'CHERRY', 'BERRY', 'MELON', 'KIWI', 'COCONUT', 'ALMOND',
  'PEANUT', 'WALNUT', 'CASHEW', 'PRETZEL', 'POPCORN', 'NACHO', 'SALSA',
  'QUESO', 'CHILI', 'CURRY', 'RAMEN', 'TOFU', 'SHRIMP', 'LOBSTER', 'CRAB',
  'SALMON', 'TUNA', 'STEAK', 'GRAVY', 'BISCUIT', 'DUMPLING', 'PIE', 'TART',
  'CUSTARD', 'PUDDING', 'GELATO', 'SORBET', 'OMELET', 'QUICHE', 'KEBAB',
];

export const WORD_SETS = [
  {
    key: 'classic', name: 'Classic', blurb: 'The standard board - a bit of everything.', words: WORDS,
  },
  {
    key: 'easy', name: 'Easy', blurb: 'Simple words a kid knows. Great first game.', words: EASY,
  },
  {
    key: 'tricky', name: 'Tricky', blurb: 'Abstract ideas and words that read two ways.', words: TRICKY,
  },
  {
    key: 'fantasy', name: 'Fantasy', blurb: 'Dragons, dungeons, and everything enchanted.', words: FANTASY,
  },
  {
    key: 'science', name: 'Science & Space', blurb: 'The lab bench and the night sky.', words: SCIENCE,
  },
  {
    key: 'food', name: 'Food & Drink', blurb: 'Everything delicious.', words: FOOD,
  },
];

export const wordSetByKey = (key) => WORD_SETS.find((s) => s.key === key) || WORD_SETS[0];

/**
 * The words a room's meta asks for. `custom` uses the generated list stored
 * on the meta (a JSON string - the house rule for arrays over RTDB); any
 * unknown or missing key falls back to Classic, so an old room or a
 * half-written meta can always deal.
 */
export const wordsForMeta = (meta) => {
  if (meta?.wordSet === 'custom' && meta.customWords) {
    try {
      const words = JSON.parse(meta.customWords);
      if (Array.isArray(words) && words.length >= 25) return words;
    } catch { /* fall through to classic */ }
  }
  return wordSetByKey(meta?.wordSet).words;
};

export const wordSetLabel = (meta) => {
  if (meta?.wordSet === 'custom') return meta.customLabel || 'Custom';
  return wordSetByKey(meta?.wordSet).name;
};
